import { XMarkIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import { toast } from "react-toastify"
import {
  useAdForgeListOverlayLayoutsQuery,
  type OverlayDoc,
  type OverlayLayout,
} from "~/ui-rtk/api/adForgeApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { ScrapedImage } from "../../types"
import { LayoutThumb, rescaleDoc } from "./BrandOverlayPanel"
import { useCustomBrandFonts } from "./useCustomBrandFonts"
import { measureMediaDims } from "./overlay-baking"

/**
 * Assign a saved layout to a card, NON-destructively: picking one stores the
 * doc (rescaled to the media's dims) on the card so it renders as a live
 * overlay in the editor. Nothing is baked here — compositing happens when
 * the ad is created (save draft). Layouts are BUILT in the Brand overlay
 * flow; this is where they get USED.
 */

interface ApplyLayoutDialogProps {
  /** The card's media — the dialog is open while this is set. */
  asset: ScrapedImage | null
  onClose: () => void
  /** Store the (media-rescaled) doc on the card. */
  onAssign: (doc: OverlayDoc, layoutId: string) => void
  /** Clear the card's current layout. */
  onRemove: () => void
}

export default function ApplyLayoutDialog({
  asset,
  onClose,
  onAssign,
  onRemove,
}: ApplyLayoutDialogProps) {
  const open = asset !== null
  useCustomBrandFonts()
  const { data: layouts, isLoading } = useAdForgeListOverlayLayoutsQuery(
    undefined,
    { skip: !open },
  )
  const [measuringId, setMeasuringId] = useState<string | null>(null)

  const assignLayout = async (layout: OverlayLayout): Promise<void> => {
    if (!asset || measuringId) return
    setMeasuringId(layout.id)
    try {
      const dims = await measureMediaDims(asset)
      onAssign(rescaleDoc(layout.doc, dims), layout.id)
      toast.success(
        `Layout "${layout.name}" applied — it bakes in when the ad is created`,
      )
      onClose()
    } catch {
      toast.error("Couldn't read the media dimensions — try again")
    } finally {
      setMeasuringId(null)
    }
  }

  return (
    <Dialog
      isOpen={open}
      handleClose={onClose}
      width={560}
      closeOnClickOutside
      classes={{
        container:
          "rounded-lg bg-system-blue-800 text-left text-white shadow-xl",
      }}
    >
      <div className="flex items-center justify-between border-b border-system-blue-700 px-system-lg py-system-md">
        <div className="flex items-center gap-system-sm min-w-0">
          {asset && (
            <img
              src={asset.thumbnailUrl || asset.url}
              alt=""
              className="w-10 h-10 rounded object-cover flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Apply a layout</h2>
            <p className="text-system-gray-300 text-xs truncate">
              Shown live over the {asset?.type === "video" ? "video" : "image"};
              baked into the media when the ad is created.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-system-gray-300 hover:text-white"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="max-h-[60vh] overflow-y-auto px-system-lg py-system-md">
        {isLoading ? (
          <div className="flex items-center justify-center gap-system-2xs py-system-lg text-system-gray-300">
            <span className="loading loading-spinner loading-sm" />
            Loading layouts…
          </div>
        ) : (layouts?.length ?? 0) === 0 ? (
          <div className="py-system-lg text-center text-system-gray-300 text-sm">
            No layouts yet. Build one first: Create with AI → Brand overlay,
            then save it as a layout.
          </div>
        ) : (
          <div className="flex flex-wrap gap-system-sm">
            {(layouts ?? []).map(layout => {
              const isActive = asset?.overlayLayoutId === layout.id
              const isMeasuring = measuringId === layout.id
              return (
                <button
                  key={layout.id}
                  type="button"
                  onClick={() => void assignLayout(layout)}
                  disabled={!!measuringId}
                  className={cn(
                    "relative flex flex-col items-center gap-1 rounded-lg border p-2 transition",
                    isActive
                      ? "border-brand/70 bg-brand/10"
                      : "border-system-blue-600 hover:border-brand/60 disabled:opacity-50",
                  )}
                  title={`Apply "${layout.name}" (${layout.aspectRatio})`}
                >
                  <LayoutThumb doc={layout.doc} />
                  <span className="max-w-[8rem] truncate text-[11px] text-system-gray-200">
                    {layout.name}
                    {isActive ? " ✓" : ""}
                  </span>
                  {isMeasuring && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60">
                      <span className="loading loading-spinner loading-sm text-brand" />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {asset?.overlayDoc && (
        <div className="flex items-center justify-between border-t border-system-blue-700 px-system-lg py-system-md">
          <span className="text-xs text-system-gray-300">
            This card has a layout applied.
          </span>
          <button
            type="button"
            onClick={() => {
              onRemove()
              onClose()
            }}
            className="rounded-md bg-system-blue-700 px-system-md py-system-2xs text-sm text-white hover:bg-system-blue-600"
          >
            Remove layout
          </button>
        </div>
      )}
    </Dialog>
  )
}
